/**
 * @voltxampere/ui — Component renderer (Sprint 12)
 *
 * Per Blueprint §12.5 + §12.6. Resolves a `'component'` scene
 * node to its Sprint 10.1 IEC symbol, applies the
 * world-space transform (translate + rotate + symbol-centre
 * offset), then dispatches to the SVG-compiled program runtime.
 *
 * Sprint 12 simplification: stroke colour is uniformly overridden
 * by the selection state. Body fills (LED translucent body, BJT
 * emitter arrow) survive untouched. Sprint 13 will revisit when
 * stamp-mode introduces semantic colour roles for op-amp ± labels
 * etc.
 *
 * Symbol-not-found is logged (no fake fallback per Kural 9) and
 * the node is skipped — the canvas would rather draw nothing than
 * lie.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { findSymbolById } from "../../symbols/index.js";
import { compileSymbol, renderProgram } from "./svg-compile.js";
const VIEWBOX_W = 40;
const VIEWBOX_H = 26;
const HALF_W = VIEWBOX_W / 2;
const HALF_H = VIEWBOX_H / 2;
export function drawComponent(ctx, node, theme) {
    const symbol = findSymbolById(node.instance.type);
    if (!symbol) {
        // No fake fallback — `console.warn` so devtools surfaces the
        // miss. Production should never hit this; fixture loaders are
        // the validation boundary.
        console.warn(`[VXA canvas] No symbol registered for component type "${node.instance.type}"`);
        return;
    }
    const program = compileSymbol(symbol.id, symbol.renderSVG());
    const strokeColor = node.selected
        ? theme.accent.brassHot
        : node.hover
            ? theme.accent.brass
            : theme.text.ivory;
    // Sprint 25 R4 lane 2 — selection / hover stroke-width emphasis.
    // `strokeOverride` already homogenises colour, but LED / Pot keep
    // their semantic brand strokes intact at the SVG level — without
    // a width boost the user has no cue that an LED is hovered (its
    // stroke is brass at rest and our `hover` colour is brass too).
    // Selected = 1.4× wider, hover = 1.15× wider, default = 1×.
    const widthFactor = node.selected ? 1.4 : node.hover ? 1.15 : 1;
    ctx.save();
    ctx.translate(node.instance.position.x, node.instance.position.y);
    if (node.instance.rotation !== 0) {
        ctx.rotate((node.instance.rotation * Math.PI) / 180);
    }
    if (node.instance.flipped) {
        ctx.scale(-1, 1);
    }
    // Symbol viewBox is (0,0,40,26); centre it on the instance origin.
    ctx.translate(-HALF_W, -HALF_H);
    renderProgram(ctx, program, strokeColor, widthFactor);
    ctx.restore();
    // Sprint 18 D20 — component label rendering. Mockup zenit-
    // platform 2.html line ~1671 ("R₁ 10k" / "V₁ 12V") declares
    // EB Garamond italic 14px brass for the label and JetBrains
    // Mono 11px ivory-dim for the value. We render only the label
    // here (the value column is computed downstream via the
    // inspector's measurement table, A4); this keeps the canvas
    // overlay readable without crowding component bodies.
    //
    // Position: below the symbol body (+1.5 × HALF_H below the
    // instance origin) so a vertical-stacked column of resistors
    // does not collide. Rotation/flip are NOT applied to the label
    // so it stays human-readable.
    if (typeof node.instance.label === "string" && node.instance.label.length > 0) {
        const labelCtx = ctx;
        labelCtx.save();
        labelCtx.fillStyle =
            strokeColor === theme.accent.brassHot ? theme.accent.brassHot : theme.accent.brass;
        labelCtx.font = "italic 14px 'EB Garamond', Georgia, serif";
        labelCtx.textAlign = "center";
        labelCtx.textBaseline = "top";
        labelCtx.fillText(node.instance.label, node.instance.position.x, node.instance.position.y + HALF_H + 6);
        labelCtx.restore();
    }
}
/**
 * Narrow guard helper — `drawComponent` works on the `component`
 * variant of `SceneNode`. Re-exporting the guard keeps the
 * dispatcher file in `renderer.ts` tidy.
 */
export function isComponentNode(node) {
    return node.kind === "component";
}
//# sourceMappingURL=component.js.map